import { ENTITY_TYPE } from 'src/features/entity/types';
import { VALIDATION_TYPE } from 'src/features/validationTypes/utils/types';
import { Visitor } from 'src/features/visitor/types';
import { LOCALE } from 'src/providers/types';
import { Translation, TranslationsWithId, CURRENCY } from 'src/types';

export enum PERIOD_TYPE {
    STAY = 'stay',
    TRANSIT = 'transit',
    EXIT = 'exit',
}

export enum VISITOR_CATEGORY {
    CLIENT = 'client',
    VIP = 'vip',
    RESIDENT = 'resident',
    STAFF = 'staff',
    SERVICE = 'service',
    SUBSCRIPTION = 'subscription',
    RESERVATION = 'reservation',
    MEMBERSHIP = 'membership',
}

export enum PERIOD_VISITOR_CATEGORY {
    CLIENT = 'client',
    SUBSCRIPTION = 'subscription',
    RESERVATION = 'reservation',
    MEMBER = 'member',
}

export interface ActiveStay {
    id: string;
    visitor: Visitor;
    visitor_owner: {
        name: string;
        id: string;
        type: ENTITY_TYPE;
    };
    period_visitor_category: PERIOD_VISITOR_CATEGORY;
    created_at: string;
    modified_at: string;
    ref: string;
    entry_time: string;
    period_entry_point: TranslationsWithId;
    period_zone: TranslationsWithId;
    period_exit_point: TranslationsWithId;
    entry_gateway: TranslationsWithId;
    main_identifier: {
        value: string;
    };
    is_manually_created: boolean;
    entry_photo: string | null;
    additional_identifiers: { value: string }[];
    duration: string;
    exit_time?: string;
    total_amounts?: {
        total_due_amount_with_tax: number;
        total_remaining_amount: number;
        total_due_amount: number;
        total_tax_amount: number;
        total_paid_amount: number;
        total_validated_time: number;
        total_validated_amount: number;
    };
    currency: CURRENCY;
}

export type ZoneStayPeriod = {
    id: string;
    zone: TranslationsWithId;
    entry_pass: {
        gateway: {
            id: string;
            translations?: Translation[];
        };
        id: string;
        time: string;
    };
    exit_pass: {
        gateway: {
            id: string;
            translations?: Translation[];
        };
        id: string;
        time: string;
    };
    created_at: string;
    modified_at: string;
    start_date: string;
    end_date: string;
};

export type ActiveStayPeriod = {
    id: string;
    created_at: string;
    modified_at: string;
    number: number;
    start: string;
    end?: string;
    duration?: string;
    type: PERIOD_TYPE;
    due_amount?: number;
    tax_amount?: number;
    due_amount_with_tax?: number;
    paid_amount: number | null;
    remaining_amount: number | null;
    currency: CURRENCY;
    stay: {
        id: string;
        period_visitor_category: string;
    };
    zones: TranslationsWithId[];
    space: string;
    entry_pass: {
        gateway: TranslationsWithId;
        id: string;
        time: string;
    };
    exit_pass: {
        gateway: TranslationsWithId;
        id: string;
        time: string;
    };
    membership?: {
        id: string;
        ref: string;
        name: string;
        membership_type_id: string;
    };
    due_amount_calculated_until?: string;
    pricing_plan: TranslationsWithId & {
        currency: CURRENCY;
    };
    zone_stay_periods: ZoneStayPeriod[];
    validated_amount: number;
    validated_time: string;
    visitor_category: VISITOR_CATEGORY;
};

export interface PastStay extends Omit<ActiveStay, 'period_entry_point' | 'period_exit_point'> {
    exit_time: string;
    exit_photo: string | null;
    exit_gateway: TranslationsWithId;
    entry_gateway: TranslationsWithId;
    total_due_amount: number;
}

export enum STAY_TYPE {
    ACTIVE = 'active',
    PAST = 'past',
}

export type ValidationTypesWithPricingPlan = {
    id: string;
    code: string;
    name: string;
    type: VALIDATION_TYPE;
    currency?: CURRENCY;
    pricing_plan?: PricingPlanRelatedToActiveStay;
};

export type PricingPlanRelatedToActiveStay = {
    id: string;
    identity: string;
    currency: CURRENCY;
    translations: {
        id: string;
        created_at: string;
        modified_at: string;
        language: LOCALE;
        name: string;
    }[];
};

export type PricingPlanChangeOptions = {
    can_be_changed?: boolean;
    validation_types_with_pricing_plans: ValidationTypesWithPricingPlan[];
    permission_based_pricing_plans: PricingPlanRelatedToActiveStay[];
};
